import { useState } from "react";

export function FounderMessageSection() {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <section className="border-y border-ink-100 bg-ink-50/60">
      <div className="mx-auto max-w-[880px] px-6 py-24 md:py-32">
        {/* ヘッダー: 左にアバター、右にタイトル */}
        <div className="flex items-center gap-6 md:gap-8">
          <div className="grid h-24 w-24 flex-shrink-0 place-items-end overflow-hidden rounded-full bg-gradient-to-br from-ink-200 to-ink-300 md:h-32 md:w-32">
            <svg className="h-full w-full" viewBox="0 0 100 100" aria-hidden="true">
              <title>開発者</title>
              <circle cx="50" cy="38" fill="#94A3B8" r="18" />
              <path d="M16 100 C 18 70, 36 56, 50 56 C 64 56, 82 70, 84 100 Z" fill="#475569" />
            </svg>
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand-600">
              開発者より
            </p>
            <h3 className="mt-3 text-[24px] font-bold leading-[1.4] tracking-tight text-ink-900 md:text-[32px]">
              <span className="whitespace-nowrap">送る側も、受け取る側も。</span>
              <br />
              <span className="whitespace-nowrap">スカウトに疲れない仕組みを。</span>
            </h3>
          </div>
        </div>

        {/* 本文（最初の2段落は常時表示） */}
        <div className="mx-auto mt-14 max-w-[720px] space-y-7 text-[15px] leading-[2] text-ink-700 md:text-[16px]">
          <p>
            私自身、CTOとしてエンジニア採用を行う中で、スカウト業務に何度も向き合ってきました。企業が候補者に熱意を持って「一緒に働きたい」と伝える
            —— スカウトの仕組み自体は本当に素晴らしいものです。
          </p>

          <p>
            しかし現実は、一人ひとりの候補者に向き合う時間が取れず、妥協してテンプレートを一括送信する。数百通送っても返信は数件。月300件という送信目標に追われながら、本来の事業開発と並行してスカウト文を書き続ける日々。
            <span className="font-bold text-ink-900">「この業務は、もう人がやる仕事ではない」</span>
            と確信しました。
          </p>
        </div>

        {/* 続きの文章（アコーディオン） */}
        <div className="mx-auto mt-7 max-w-[720px]">
          {isExpanded ? (
            <div className="space-y-7 text-[15px] leading-[2] text-ink-700 md:text-[16px]">
              <p>
                一方で、転職希望者として登録している立場からも感じていました。明らかに一括送信されたスカウトが届くと、どれほど事業内容が魅力的でも興味を失う。丁寧に書かれたように見えても、自分の経歴とほんの少しズレた記述があると「読んでくれていないな」とわかる。
              </p>

              <p>
                送る側も、受け取る側も、今のスカウトに疲れています。良い企業が良いサービスを提供していても、一斉送信のスカウトではその魅力が候補者に伝わらない。これが最大の問題だと考えています。
              </p>

              <p>
                ChatGPTなどの汎用AIを使って個別生成する企業も出てきましたが、自社の募集要項に対して本当に最適なスカウト文が生成できているかは疑問です。さらに深刻なのは、候補者の個人情報を汎用AIチャットに入力する際のコンプライアンスリスク。モデルプロバイダーによる学習を防ぐ設定の有無まで意識している企業は、まだ少ないのが現状です。
              </p>

              <p>
                こうした課題感からスカウトメールAIを開発しました。AIに任せるべきは、募集要項と候補者のレジュメを正確に読み解き、どこがどうマッチしているかを文章として作成すること。人間よりも正確で、ズレのないスカウト文をAIが生成すれば、
                <span className="font-bold text-ink-900">
                  人事担当者の業務負荷を削減しながら、候補者の「がっかり」も減らせる
                </span>
                。それがこのサービスの原点です。
              </p>

              <p>
                AIスカウトの是非が議論される中で見えてきた本質があります。候補者が本当に嫌なのはAIではなく、「自分が雑に扱われた」という感覚です。大切なのは、なぜあなたに送ったのか、どこがマッチしているのかを正確に伝えること。それはむしろ、人間よりもAIの方が得意な領域です。
              </p>

              <p>
                スカウトメールAIにはスキルマッチ機能を搭載しています。募集要項と候補者のレジュメを照合し、高いマッチレベルの候補者にのみスカウトを送信できる。「送信数の最大化」ではなく「マッチ精度の最大化」を設計思想に据えることで、求職者にとっても人事担当者にとっても、良い出会いと工数削減の両立を目指しています。
              </p>

              <p className="font-medium text-ink-900">
                送る側が疲弊せず、受け取る側ががっかりしない。そんなスカウト体験を実現するために、このサービスを作りました。
              </p>
            </div>
          ) : (
            <div className="relative" style={{ height: 110 }}>
              {/* 下地: 軽くブラーをかけたテキスト（上端でも読み切れない程度） */}
              <div
                aria-hidden="true"
                className="absolute inset-0 space-y-7 overflow-hidden text-[15px] leading-[2] text-ink-700 md:text-[16px]"
                style={{ userSelect: "none", filter: "blur(1.5px)" }}
              >
                <p>
                  一方で、転職希望者として登録している立場からも感じていました。明らかに一括送信されたスカウトが届くと、どれほど事業内容が魅力的でも興味を失う。丁寧に書かれたように見えても、自分の経歴とほんの少しズレた記述があると「読んでくれていないな」とわかる。
                </p>
                <p>
                  送る側も、受け取る側も、今のスカウトに疲れています。良い企業が良いサービスを提供していても、一斉送信のスカウトではその魅力が候補者に伝わらない。
                </p>
              </div>

              {/* オーバーレイ: ブラー済み + 下にいくほど不透明（=ブラー強）になるマスク */}
              <div
                aria-hidden="true"
                className="absolute inset-0 space-y-7 overflow-hidden text-[15px] leading-[2] text-ink-700 md:text-[16px]"
                style={{
                  filter: "blur(6px)",
                  userSelect: "none",
                  WebkitMaskImage:
                    "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 100%)",
                  maskImage: "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 100%)",
                }}
              >
                <p>
                  一方で、転職希望者として登録している立場からも感じていました。明らかに一括送信されたスカウトが届くと、どれほど事業内容が魅力的でも興味を失う。丁寧に書かれたように見えても、自分の経歴とほんの少しズレた記述があると「読んでくれていないな」とわかる。
                </p>
                <p>
                  送る側も、受け取る側も、今のスカウトに疲れています。良い企業が良いサービスを提供していても、一斉送信のスカウトではその魅力が候補者に伝わらない。
                </p>
              </div>

              {/* 背景フェード（下に向かって白く） */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    "linear-gradient(to bottom, rgba(248,250,252,0) 0%, rgba(248,250,252,0.6) 60%, rgba(248,250,252,1) 100%)",
                }}
              />

              {/* ブラーの最上部に「もっと読む」ボタン */}
              <div className="absolute inset-x-0 top-0 flex justify-center">
                <button
                  type="button"
                  onClick={() => setIsExpanded(true)}
                  className="relative inline-flex items-center gap-2 rounded-full border border-ink-200 bg-white px-6 py-3 text-[13px] font-semibold text-ink-800 shadow-soft transition-colors hover:bg-ink-50"
                >
                  もっと読む（長文です）
                  <span className="mi text-[16px] text-ink-500">expand_more</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* 署名 */}
        <div className="mx-auto mt-12 max-w-[720px] border-t border-ink-200 pt-6 text-right">
          <p className="text-[13px] text-ink-500">代表 / 開発者</p>
          <p className="mt-1 text-[15px] font-bold text-ink-900">黒田 裕己</p>
        </div>
      </div>
    </section>
  );
}
